import React, { useRef, useState } from 'react';
import classnames from 'classnames';
import { useMemoizedFn, useRequest } from 'ahooks';
import CardSlot from '@gp/components/CardSlot';
import GeneralModal from '@gp/components/GeneralModal';
import Departments from '@gp/pages/Pipeline/ProjectDetails/Stakeholder/components/StakeholderDetails/components/Departments';
import { IOrganizationsPropsType } from '@gp/pages/Pipeline/ProjectDetails/Stakeholder/types';
import { DEPARTMENT_NAME_MAPS } from '@gp/pages/Pipeline/ProjectDetails/Stakeholder/consts';
import { formatDateStr } from '@gp/utils/common';
import UpdateEditForm from '../UpdateEditForm/index';
import { reportOperationLog } from '@gp/services/global';
import { editStakeholders } from '@gp/services/stakeholder';
import { ALL_REPORT_OPERATION_LOG } from '@gp/constants/operationLogParams';

import './index.scss';

const ICON_LIST = [
  {
    isText: true,
    icon: 'pencil-m',
    context: 'Edit',
  },
];

const Organizations: React.FC<IOrganizationsPropsType> = (props) => {
  const { organizations, projectId, isEditable, stakeholderListRun, onRefreshProjectDetails } = props;

  const [visible, setVisible] = useState<boolean>(false);
  const [disabledConfirm, setDisabledConfirm] = useState<boolean>(true);
  const [loadingConfirm, setLoadingConfirm] = useState<boolean>(false);
  const [editList, setEditList] = useState<any>(null);
  const modalListRef = useRef<StakeHolderModalForwardProps>(null);

  const { runAsync: editStakeholdersRun } = useRequest(editStakeholders, { manual: true });

  const open = useMemoizedFn((currentList: any) => {
    setEditList(currentList);
    setVisible(true);
  });

  const close = useMemoizedFn(() => {
    setVisible(false);
  });

  const handleConfirm = useMemoizedFn(async () => {
    const params = modalListRef.current?.getSubmitResult();
    // 如果弹窗内信息有误，不关闭弹窗
    if (params === 'error') return;
    reportOperationLog(ALL_REPORT_OPERATION_LOG.DETAIL_STAKEHOLDER_IEGG_EDIT_SUBMIT, 'button');
    // 没有任何更改，直接关闭
    if (!params?.add && !params?.delete && !params?.update) {
      close();
      return;
    }
    setLoadingConfirm(true);
    try {
      const res: any = await editStakeholdersRun({
        ...params,
        project_id: projectId || '',
      });
      if (res?.status === 200 && res?.data?.code === 200) {
        close();
        // 重新请求页面数据
        stakeholderListRun({ project_id: projectId });
        onRefreshProjectDetails?.();
      }
    } finally {
      setLoadingConfirm(false);
    }
  });

  const handleCancel = useMemoizedFn(() => {
    close();
  });

  return (
    <>
      {organizations?.map((organization: any) => {
        const owners = organization?.owner_list;
        const organizationKey = organization?.organization;
        const organizationName = DEPARTMENT_NAME_MAPS[organizationKey];
        const organizationClassName = `dbg-pipeline-project-detail-stakeholder-org__${organizationKey}`;
        if (!Array.isArray(owners) || !owners.length)
          return (
            <CardSlot
              key={organizationKey}
              className={classnames('coming-soon dbg-stakeholder-no-data-card', organizationClassName)}
              title={<div className="dbg-org-title">{organizationName}</div>}
              iconList={isEditable ? ICON_LIST : []}
              getChange={(value: any) => {
                if (value == 'pencil-m') {
                  open(organization);
                }
              }}
            >
              <span className="coming-soon-text">
                This project has no stakeholder information yet.
                {isEditable && (
                  <>
                    &nbsp;Please click to&nbsp;
                    <a
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        open(organization);
                      }}
                    >
                      Add New Stakeholder
                    </a>
                    .
                  </>
                )}
              </span>
            </CardSlot>
          );
        const organizationUpdateTimeFormatted = formatDateStr(organization?.update_time);
        return (
          <CardSlot
            key={organizationKey}
            title={
              <div className="dbg-org-title">
                {organizationName}
                {!!organizationUpdateTimeFormatted && (
                  <span className="dbg-org-subtitle">Updated on {organizationUpdateTimeFormatted}</span>
                )}
              </div>
            }
            iconList={isEditable ? ICON_LIST : []}
            getChange={(value: any) => {
              if (value == 'pencil-m') {
                open(organization);
              }
            }}
            className={classnames('org-item', organizationClassName)}
          >
            <Departments organization={organizationKey} departments={organization?.owner_list} />
          </CardSlot>
        );
      })}
      {/* 编辑弹窗 */}
      <GeneralModal
        visible={visible}
        title="Update Info"
        isFont14={true}
        className="dbg-general-edit-modal dbg-stakeholder-edit-modal"
        modalSize="auto"
        disabledConfirm={disabledConfirm}
        loadingConfirm={loadingConfirm}
        // modalSize="m"
        onConfirm={handleConfirm}
        onClose={handleCancel}
        content={
          <UpdateEditForm
            ref={modalListRef}
            isEntryEdit={visible}
            data={editList}
            onConfirmDisabledChange={(v) => setDisabledConfirm(v)}
          />
        }
      />
    </>
  );
};

export default React.memo(Organizations);
